import {cdnImage} from '@/util/cdnImage';
import {MUIButton, MUIContainer} from '@/components/MUI-Components';
import {ON_DEMAND_META_DATA} from '@/util/metaData/mobile';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import dynamic from 'next/dynamic';
import PageHead from '@/components/PageHead';
import Typography from '@mui/material/Typography';

// Migrated from globals.scss `.demand-revenue-img-wrapper` (nested `img` rule). Sole owner: this
// page's own `Decoding` instance below. `Decoding` has no root-level `sx` passthrough, so the
// former descendant `img` selector is reproduced via `Decoding`'s existing `imgSx` prop, which
// `DisplayImage` applies directly to the rendered `<img>`/`<Image>` (no wrapper element) — same
// element, same declaration, zero behavior change. No new passthrough prop was added to any shared
// component for this migration.
const demandRevenueImgWrapperSx = {
    maxWidth: '530px !important' // no exact token match — preserved for parity
} as const;

// ─── getStaticProps ─────────────────────────────────────────────────────────
export const getStaticProps = async () => {
    const {
        onDemandAdoption,
        onDemandAppBannerPoint,
        onDemandAppDecodingData,
        onDemandChooseBody,
        onDemandChooseContent,
        onDemandIndustryData,
        onDemandKeyFeatureData,
        onDemandMobileProcess,
        onDemandPricingData,
        onDemandRevenueApp,
        onDemandServiceData,
        onDemandTechnologyData,
        onDemandFAQData,
        seatBumButtonData,
        seatBumCheckPoint,
        skySalonButtonData,
        skySalonCheckPoint,
        revenueOnDemandData
    } = await import('@/util/contents/on-demand-data');

    const {mobileApplicationBannerBottom} = await import('@/util/contents/mobile-app-data');

    const {reactJSKfcCheckPoint, reactKfcButtonData} = await import('@/util/contents/react-data');

    return {
        props: {
            onDemandAdoption,
            onDemandAppBannerPoint,
            onDemandAppDecodingData,
            onDemandChooseBody,
            onDemandChooseContent,
            onDemandIndustryData,
            onDemandKeyFeatureData,
            onDemandMobileProcess,
            onDemandPricingData,
            onDemandRevenueApp,
            onDemandServiceData,
            onDemandTechnologyData,
            onDemandFAQData,
            seatBumButtonData,
            seatBumCheckPoint,
            skySalonButtonData,
            skySalonCheckPoint,
            revenueOnDemandData,
            mobileApplicationBannerBottom,
            reactJSKfcCheckPoint,
            reactKfcButtonData
        }
    };
};

const LetDiscuss = dynamic(() => import('@/components/Ui-components/Lets-discuss'));
const LatestInsights = dynamic(() => import('@/components/Ui-components/latest-insights'));
const Testimonials = dynamic(() => import('@/components/Ui-components/Testimonials'));
const Questions = dynamic(() => import('@/components/plain-sections/Questions'));
const Decoding = dynamic(() => import('@/components/Ui-components/Decoding'));
const auditImg = cdnImage('On-demand-app/On-Demand-Economy.webp');
const DemandKeyFeature = dynamic(() => import('@/components/Ui-components/demand-feature'));
const LatestCTA = dynamic(() => import('@/components/Ui-components/LatestCta'));
const DynamicScreens = dynamic(() => import('@/components/Ui-components/Dynamic-screens'));
const kfc = cdnImage('mobile-app-new-images/KFC-delivery-app.webp');
import Container from '@mui/material/Container';
const IndustryLeaders = dynamic(() => import('@/components/Ui-components/IndustryLeaders'));
const Pricing = dynamic(() => import('@/components/Ui-components/pricing'));
const CardTechnology = dynamic(() => import('@/components/Ui-components/card-technology'));
const MobileProcess = dynamic(() => import('@/components/Ui-components/MobileProcess'));
const development = cdnImage('On-demand-app/On-Demand-App-Development-Process.svg');
const OnDemandAdoption = dynamic(() => import('@/components/Ui-components/on-demand-adoption'));
const OurMakeChoice = dynamic(() => import('@/components/Ui-components/OurMakeChoice'));
const HelperSection = dynamic(() => import('@/components/Ui-components/helper-section'));
const seatBum = cdnImage('On-demand-app/Seat-Bum.webp');
const skySalon = cdnImage('Hire-dot-net-developer/Sky-Salon-Scheduler.webp');
const appRevenue = cdnImage('On-demand-app/On-Demand-App-Revenue.webp');
const EndDevelopment = dynamic(() => import('@/components/Ui-components/EndDevelopmentService'));
import AggregateScript from '@/components/Common/Aggregate-script';
import {CDN_URL, SITE_URL} from '@/constants/urls';

// Above-the-fold sections are imported statically, not with next/dynamic: a dynamic section renders
// nothing until its chunk arrives after an in-site navigation, then pushes the page down (CLS).
import Banner from '@/components/Ui-components/Banner';
import DisplayImage from '@/components/Ui-components/display-image';

const addLaunch = cdnImage('On-demand-app/app-launch.webp');

const OnDemand = (props: any) => {
    const {
        openForm,
        onDemandAdoption,
        onDemandAppBannerPoint,
        onDemandAppDecodingData,
        onDemandChooseBody,
        onDemandChooseContent,
        onDemandIndustryData,
        onDemandKeyFeatureData,
        onDemandMobileProcess,
        onDemandPricingData,
        onDemandRevenueApp,
        onDemandServiceData,
        onDemandTechnologyData,
        onDemandFAQData,
        seatBumButtonData,
        seatBumCheckPoint,
        skySalonButtonData,
        skySalonCheckPoint,
        revenueOnDemandData,
        mobileApplicationBannerBottom,
        reactJSKfcCheckPoint,
        reactKfcButtonData
    } = props;

    return (
        <Box>
            <PageHead meta={ON_DEMAND_META_DATA} url="/on-demand-app-development" />
            <Banner
                priority={true}
                handleClickOpen={openForm}
                heading="On Demand App Development Company"
                subheading="Missed service windows and fragmented digital tools can hold your growth back. Our on-demand app development services eliminate inefficiencies by delivering fast, scalable, and user-first solutions tailored for real-time consumer expectations. From product ideation to launch, we bring cross-functional expertise to build mobile apps that perform—backed by strong UI/UX, analytics, and monetization logic."
                button="Request a Consultation"
                image={cdnImage('On-demand-app/On-Demand-App-Development.webp')}
                bannerClass=""
                // bannerBackground="new-home-banner-background"
                alt="On Demand App Development Company"
                DataPoints={onDemandAppBannerPoint}
            />
            <Box className="sub-banner-wrapper sub-banner-bottom-wrapper">
                <MUIContainer>
                    <Grid container spacing={2} alignItems="center" justifyContent="space-between" className="white-label-bottom-container">
                        <Grid item md={2} lg={1.7} sm="auto" xs={12}>
                            <Typography className="banner-bottom-heading">Trusted By Leading Brands</Typography>
                        </Grid>
                        {mobileApplicationBannerBottom?.map((data: any, index: number) => (
                            <Grid item sm="auto" xs={6} className="sub-banner-images" key={index + 1}>
                                <Box className="images-banner-bottom">
                                    <DisplayImage src={data.src} alt={data.alt} priority={true} />
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </MUIContainer>
            </Box>
            <Grid item xs={12} className="bottom-margin top-space decording-main-wrapper mobile-top-padding mobile-bottom-margin">
                <MUIContainer>
                    <Decoding
                        imageSrc={auditImg}
                        alt="Capitalize on the On-Demand Economy to Redefine Service Delivery"
                        heading="Capitalize on the On-Demand Economy to Redefine Service Delivery"
                        subHeading={onDemandAppDecodingData}
                        imgSx={{maxWidth: 530}}
                        buttonText="Speak to Our Consultants"
                        openForm={openForm}
                    />
                </MUIContainer>
            </Grid>

            <Box>
                <EndDevelopment
                    heading="Our All-Inclusive On-Demand App Development Services"
                    subheading="We offer a full suite of on-demand app development services to help you build, launch, and scale faster. Every solution we deliver is crafted to meet your business logic, technical needs, and customer journey across iOS, Android, and web platforms."
                    contentData={onDemandServiceData}
                    openForm={openForm}
                    Cta="Accelerate Your App Launch with Expert-Built Solutions"
                    robotImage={false}
                    contactCard={addLaunch}
                />
            </Box>
            <Grid item xs={12} className="bottom-margin top-space decording-main-wrapper mobile-top-padding mobile-bottom-margin">
                <MUIContainer>
                    <Decoding
                        imageSrc={appRevenue}
                        alt="Drive 10X Revenue Uplift with On-Demand App Development"
                        heading="Drive 10X Revenue Uplift with On-Demand App Development"
                        subHeading={onDemandRevenueApp}
                        className="demand-revenue-img-wrapper"
                        imgSx={demandRevenueImgWrapperSx}
                        buttonText="Get a Custom ROI Plan"
                        openForm={openForm}
                    />
                </MUIContainer>
            </Grid>
            <Grid
                className="demand-key-feature-wrapper top-space mobile-top-padding bottom-spacing mobile-bottom-padding helper_section_parent"
                sx={{backgroundColor: '#f4f8fb !important'}}
            >
                <DemandKeyFeature
                    heading="Key Features of Our On-Demand App Solutions"
                    subheading="Built with every stakeholder in mind, our on-demand app solutions deliver seamless experiences for customers, service providers, and operations teams, all from a unified platform."
                    contentData={onDemandKeyFeatureData}
                />
            </Grid>
            <Box className="demand-adoption-section-wrapper bottom-spacing top-space mobile-top-padding">
                <OnDemandAdoption
                    heading="On-Demand App Development Use Cases We Build Around"
                    subheading="We build custom on-demand app solutions tailored to your industry, audience, and business model—whether you're launching a hyperlocal service, real-time delivery platform, or multi-vendor marketplace. Explore the diverse use cases we’ve brought to life."
                    contentData={onDemandAdoption}
                />
            </Box>

            <Box className="bottom-spacing  mobile-bottom-padding ">
                <LatestCTA
                    heading="$335B+ On-Demand Market—Where Does Your Business Thrive?"
                    subheading="From delivery to healthcare to mobility, the on-demand space is scaling faster. Let’s define a high-impact use case that drives growth and positions you to lead."
                    buttonText="Book Your Strategy Session"
                    openForm={openForm}
                    className="blue-background-cta"
                />
            </Box>
            <Grid item xs={12} className="dynmaic-white-label-wrapper  bottom-margin dynamic-bep20-wrapper mobile-bottom-margin ">
                <MUIContainer>
                    <Box className="spectrum-header-wrapper solutions">
                        <Typography className="spectrum-heading color-black " component="h2">
                            Our Portfolio of On-Demand App Solutions
                        </Typography>
                        <Box className="divder-wrapper">
                            <hr className="divider-line" />
                        </Box>
                        <Typography className="spectrum-sub-heading color-black text-align-left" component="p">
                            Our development team has delivered high-performing apps for healthcare providers, logistics firms, home service
                            startups, and more. See how our agile-driven builds support scale, speed, and reliability—core to any successful
                            on-demand product.
                        </Typography>
                    </Box>
                    <DynamicScreens
                        imageSrc={kfc}
                        heading="Optimizing Restaurant Operations with KFC Delivery Store App"
                        subPoints={[
                            'Debut Infotech partnered with KFC to develop a restaurant delivery management app, optimizing the order-taking process, tracking deliveries, and enhancing customer experiences.'
                        ]}
                        reverseOrder={true}
                        alt="KFC"
                        checkItems={reactJSKfcCheckPoint}
                        buttonData={reactKfcButtonData}
                        openForm={openForm}
                        buttonLabel="Request a Consultation"
                        buttonClass="nft-button"
                        imageClass="dynamic-exchange-image"
                    />
                    <DynamicScreens
                        imageSrc={skySalon}
                        heading="Sky Salon Scheduler – Redefining Appointment Booking for Salons"
                        subPoints={[
                            'Debut Infotech partnered with Sky Salon Scheduler to develop an intuitive, real-time booking app for salons and spas. Designed for both clients and staff, the platform enables seamless appointment scheduling, personalized service selection, staff availability syncing, and automated reminders—eliminating manual coordination and no-shows.'
                        ]}
                        reverseOrder={false}
                        alt="KFC"
                        checkItems={skySalonCheckPoint}
                        buttonData={skySalonButtonData}
                        openForm={openForm}
                        buttonLabel="Request a Consultation"
                        buttonClass="helper-card-button"
                        imageClass="dynamic-exchange-image"
                    />
                    <DynamicScreens
                        imageSrc={seatBum}
                        heading="Seat Bum – On-Demand Food Delivery for Live Stadium Events"
                        subPoints={[
                            'Seat Bum is an event-focused delivery platform developed for sports stadiums and live entertainment venues. Built by Debut Infotech, the app allows users to order food and beverages from their seats, track deliveries in real time, and avoid missing a single minute of the game.'
                        ]}
                        reverseOrder={true}
                        alt="KFC"
                        checkItems={seatBumCheckPoint}
                        buttonData={seatBumButtonData}
                        openForm={openForm}
                        buttonLabel="Request a Consultation"
                        buttonClass="nft-button"
                        imageClass="dynamic-exchange-image"
                    />
                </MUIContainer>
            </Grid>
            <Grid item xs={12} className="helper-section">
                <Box className="helper_section_parent bottom-spacing top-space mobile-top-padding mobile-bottom-padding">
                    <MUIContainer>
                        <Box className="spectrum-header-wrapper solutions">
                            <Typography className="spectrum-heading color-black our-service-main-heading text-align-left" component="h2">
                                Revenue Models You Can Plug In
                            </Typography>
                            <Box className="divder-wrapper">
                                <hr className="divider-line divider-line-left" />
                            </Box>
                            <Box className="sub-heading-box" pb={4}>
                                <Typography className="spectrum-sub-heading color-black spacing">
                                    From tiered memberships to real-time bidding, your app can earn while it serves. Explore monetization
                                    strategies designed to align with your platform’s traffic, service model, and pricing flexibility—no
                                    matter the vertical you operate in.
                                </Typography>
                            </Box>
                        </Box>
                        <Grid container spacing={2}>
                            <HelperSection data={revenueOnDemandData} className="home-helper-card" />
                        </Grid>
                    </MUIContainer>
                </Box>
            </Grid>
            <Grid item xs={12}>
                <Box className="renowed-client-wrapper  bottom-spacing mobile-bottom-padding top-space mobile-top-padding">
                    <MUIContainer>
                        <IndustryLeaders
                            heading="Industries We Serve"
                            subheading="From fast-scaling startups to global enterprises, businesses across sectors are reimagining how they deliver services—instantly, intelligently, and at scale. As a leading on-demand app development company, we build tailored solutions that help brands streamline operations, improve customer experience, and drive real-time value across complex service ecosystems."
                            data={onDemandIndustryData}
                            className="industry-leader-content-wrapper industry-leader-content-animation"
                            classNm="industry-leader-content-side-space"
                        />
                    </MUIContainer>
                </Box>
            </Grid>
            <Box>
                <LatestCTA
                    heading="Do you need a custom mobile app or have specific requirements to build a mobile app?"
                    subheading="We've got you covered! No matter the industry, we design and develop mobile apps that solve real business challenges. Share your requirements, and our team will craft a solution that delivers measurable results."
                    buttonText="Get a Free Consultation"
                    openForm={openForm}
                    className="blue-background-cta"
                />
            </Box>
            <Grid item xs={12} className="bottom-spacing mobile-bottom-padding">
                <Container maxWidth="lg">
                    <Pricing
                        heading="Hire On-Demand Developers with Models That Fit Your Workflow"
                        subheading="Whether you're building a real-time delivery app, scaling a ride-hailing platform, or optimizing UI flows for customer-facing services, our tailored engagement models help you <a target='_blank' href='/hire-mobile-app-developers'>hire on-demand app developers</a> who align perfectly with your goals, timeline, and budget."
                        pricingData={onDemandPricingData}
                        className="remote-pricing-developer-card price-dot-net-card hire-pricing"
                        onClick={openForm}
                    />
                    <Box mt={2} className="prompt-pricing-button">
                        <MUIButton onClick={openForm} label="Claim Your Free Dev Consultation" className="helper-card-button" />
                    </Box>
                </Container>
            </Grid>
            <Grid
                item
                xs={12}
                className="bottom-spacing generative-tech-wrapper mobile-bottom-padding top-space mobile-top-padding helper_section_parent"
            >
                <CardTechnology
                    heading="Technologies We Trust for On-Demand App Development"
                    subheading="Our on-demand app development services are built on a reliable, modular tech stack designed to support real-time performance, scalability, and cross-platform accessibility. From smart logistics to seamless payments and AI-based dispatch, we use the tools that today’s fastest-growing platforms trust to scale and deliver."
                    data={onDemandTechnologyData}
                />
            </Grid>
            <Box className="bottom-spacing process-mobile-bottom-padding top-space mobile-top-padding">
                <MUIContainer>
                    <Box className="spectrum-header-wrapper solutions">
                        <Typography className="spectrum-heading color-black text-align-left" component="h2">
                            Our End-to-End Process for On-Demand App Development
                        </Typography>
                        <Box className="divder-wrapper">
                            <hr className="divider-line divider-line-left" />
                        </Box>
                        <Box className="sub-heading-box" pb={4}>
                            <Typography className="spectrum-sub-heading color-black spacing text-align-left" component="p">
                                Recognized as a trusted on-demand app development company, we follow a sprint-based agile methodology that
                                balances speed with precision—delivering scalable, real-time platforms through flexible engagements. Our
                                process is structured to meet business goals while reducing time-to-market and project risks.
                            </Typography>
                        </Box>
                    </Box>
                    <Box textAlign="center">
                        <DisplayImage
                            src={development}
                            alt="Our DeFi Staking Platform Development"
                            className="approach-main-img approach-web-img"
                        />
                    </Box>
                    <Box className="approach-main-img approach-mobile-img">
                        <MobileProcess mobileProcess={onDemandMobileProcess} />
                    </Box>
                </MUIContainer>
            </Box>
            <OurMakeChoice
                heading="Why Choose Debut Infotech"
                subheading="a Leading On-Demand App Development Company?"
                body={onDemandChooseBody}
                contentData={onDemandChooseContent}
            />
            <Grid item xs={12}>
                <LetDiscuss
                    heading="Trusted by startups and Fortune 500s alike"
                    subheading="We’ve built industry-grade on-demand platforms for food, healthcare, logistics, and retail—delivering speed, stability, and scale."
                    btnLabel="Talk to an On-Demand Expert"
                    openForm={openForm}
                    className="white-label-sprill"
                />
            </Grid>
            <Grid item xs={12} className="question-parent margin top-space mobile-top-padding">
                <MUIContainer>
                    <Questions accordianData={onDemandFAQData} />
                </MUIContainer>
            </Grid>

            <Grid item xs={12}>
                <Box className="top-space mobile-top-padding bg-blue-background">
                    <Testimonials heading="Our Clients Describe Us Best" />
                </Box>
            </Grid>

            <Grid item xs={12} className="latest-insights top-space mobile-top-padding">
                <LatestInsights id="22" />
            </Grid>
            <script
                type="application/ld+json"
                id="defi-staking-platform-development"
                dangerouslySetInnerHTML={{
                    __html: JSON.stringify({
                        '@context': 'https://schema.org',
                        '@type': 'FAQPage',
                        mainEntity: [
                            {
                                '@type': 'Question',
                                name: 'What is an on-demand app?',
                                acceptedAnswer: {
                                    '@type': 'Answer',
                                    text: 'An on-demand app connects users with services or products in real-time, such as food delivery, ride-hailing, or home repairs. These apps streamline booking, payment, and fulfillment—offering businesses a digital channel to monetize services, improve accessibility, and retain customers through personalized experiences.'
                                }
                            },
                            {
                                '@type': 'Question',
                                name: 'How to build an on-demand app?',
                                acceptedAnswer: {
                                    '@type': 'Answer',
                                    text: 'To build an on-demand app, define your business model, identify core features, choose your tech stack, and hire a development team. Use agile methodology for development, integrate secure payment gateways, deploy to app stores, and monitor performance for ongoing optimization.'
                                }
                            },
                            {
                                '@type': 'Question',
                                name: 'How much does it cost to develop an on-demand app?',
                                acceptedAnswer: {
                                    '@type': 'Answer',
                                    text: 'The cost to develop an on-demand app ranges from $25,000 to $120,000. Pricing depends on features, platform support, integrations, and app complexity. Cross-platform development, real-time tracking, and payment modules may increase cost. Consult a trusted on-demand app development company for accurate estimates aligned with your goals and market fit.'
                                }
                            },
                            {
                                '@type': 'Question',
                                name: 'How much does it cost to develop an on-demand delivery app?',
                                acceptedAnswer: {
                                    '@type': 'Answer',
                                    text: 'On-demand delivery app development costs between $30,000 and $150,000. Costs vary based on delivery tracking, multi-role interfaces, payment integration, and platform scalability. Features like route optimization and analytics impact price. For budget clarity, connect with an experienced delivery app development firm offering detailed scopes and fixed-cost models.'
                                }
                            },
                            {
                                '@type': 'Question',
                                name: 'How long does it take to build an on-demand app?',
                                acceptedAnswer: {
                                    '@type': 'Answer',
                                    text: 'The development timeline for an on-demand app depends on complexity. A basic MVP takes 4–6 weeks, while full-scale solutions with custom workflows, third-party integrations, and dashboards can take 12–16 weeks. Agile sprints ensure faster feedback, early launches, and continuous improvement without compromising quality or scalability across devices and platforms.'
                                }
                            },
                            {
                                '@type': 'Question',
                                name: 'Will it integrate with my existing ERP/CRM?',
                                acceptedAnswer: {
                                    '@type': 'Answer',
                                    text: 'Yes, our on-demand app development solutions are engineered for seamless ERP integration. We support real-time sync with platforms like SAP, Oracle NetSuite, Microsoft Dynamics, and custom ERPs via RESTful APIs, webhooks, or middleware connectors. We also build custom adapters for legacy systems to ensure unified data exchange across your inventory, order management, finance, and HR modules—so you get a centralized operational view without disrupting current processes.'
                                }
                            },
                            {
                                '@type': 'Question',
                                name: 'Can I white-label and license this later?',
                                acceptedAnswer: {
                                    '@type': 'Answer',
                                    text: "Absolutely. All our on-demand apps are built with modular architecture and custom branding layers to support white-labeling and licensing. We offer full source-code ownership, customizable UI/UX templates, tenant-based infrastructure (for SaaS rollouts), and licensing strategies that let you monetize the solution as a platform. Whether you're planning to offer this as B2B SaaS or private-label software, we help you scale your IP legally and profitably."
                                }
                            },
                            {
                                '@type': 'Question',
                                name: 'Is the app cloud-agnostic?',
                                acceptedAnswer: {
                                    '@type': 'Answer',
                                    text: 'Yes, our app infrastructure is designed to be cloud-agnostic and multi-cloud ready. Whether you prefer AWS, Microsoft Azure, Google Cloud, DigitalOcean, or even hybrid/on-prem environments, we use containerization (Docker/Kubernetes) and scalable microservices to ensure your app remains portable, performant, and vendor-agnostic. This gives you the flexibility to optimize for cost, compliance, or regional data residency—without getting locked into a single provider.'
                                }
                            },
                            {
                                '@type': 'Question',
                                name: 'How secure is the payment flow?',
                                acceptedAnswer: {
                                    '@type': 'Answer',
                                    text: 'Security is built into every layer of our payment workflows. We implement PCI-DSS-compliant payment gateways, tokenization, end-to-end encryption, and multi-factor authentication (MFA). Whether you’re using Stripe, Razorpay, Apple Pay, or custom banking APIs, we enforce role-based permissions, fraud detection algorithms, and real-time anomaly monitoring to keep every transaction secure, reliable, and fully auditable—ensuring regulatory readiness and customer trust.'
                                }
                            },
                            {
                                '@type': 'Question',
                                name: 'What’s the monthly cost to scale on-demand app solutions?',
                                acceptedAnswer: {
                                    '@type': 'Answer',
                                    text: 'Your monthly cost will depend on active users, cloud consumption, third-party API usage, and support tier. On average, businesses spend between $500 to $5,000/month post-deployment for cloud hosting, CDN, third-party integrations (like Maps or SMS), and tiered maintenance. We offer usage-based billing models, so you only pay for what you consume—ensuring optimal ROI. Our team will also help you forecast operating costs with precision before launch.'
                                }
                            },
                            {
                                '@type': 'Question',
                                name: 'How will I train vendors/customers?',
                                acceptedAnswer: {
                                    '@type': 'Answer',
                                    text: 'We provide full-scale training and onboarding support through role-based documentation, in-app tooltips, video walkthroughs, and interactive tutorials tailored for both vendors and customers. Additionally, we set up multi-lingual knowledge bases, live chat integrations, and admin-accessible onboarding flows so your platform remains self-serve, scalable, and frictionless. If needed, we also offer custom LMS modules or microlearning content for ongoing support across locations.'
                                }
                            },
                            {
                                '@type': 'Question',
                                name: 'Can I build a super app with multi-services?',
                                acceptedAnswer: {
                                    '@type': 'Answer',
                                    text: 'Yes, we offer super app development with modular service layers—such as food delivery, taxi booking, pharmacy, and payments—all under one platform. Users can access multiple services through a unified interface with single sign-on and cross-feature compatibility.'
                                }
                            }
                        ]
                    })
                }}
            />
            <AggregateScript
                name="On-Demand App Development"
                image={`${CDN_URL}?url=%2Fimages%2FOn-demand-app%2FOn-Demand-App-Development-Company.webp&w=750&q=75`}
                description="Launch feature-rich on-demand apps tailored to your business needs. Our expert developers build scalable, user-centric solutions for real-time service delivery, seamless user experience, and rapid market entry across industries like healthcare, logistics, food delivery, and more."
                offers={{
                    '@type': 'AggregateOffer',
                    url: `${SITE_URL}/on-demand-app-development`,
                    priceCurrency: 'USD',
                    lowPrice: '50000',
                    highPrice: '100000',
                    offerCount: '10'
                }}
                aggregateRating={{
                    '@type': 'AggregateRating',
                    ratingValue: '4.8',
                    bestRating: '5',
                    worstRating: '2',
                    ratingCount: '499'
                }}
            />
        </Box>
    );
};
export default OnDemand;
OnDemand.Layout = 'Main';
